import { createContext, FunctionComponent } from 'preact';
import { useContext, useMemo } from 'preact/hooks';
import type { StreamProvider } from '@vgtv/api-client';
import { SpsPublication } from '@schibsted/sps-sdk';

import { createClient } from '../services/httpClient';
import { createSvpApi, SvpApiClient } from '../services/svpApi';
import { createRelatedApi, RelatedApiClient } from '../services/relatedApi';
import { createVGTVApi, VGTVApiClient } from '../services/vgtvApi';
import { createSpsApi, SpsApiClient } from '../services/sps/spsApi';
import { createPlaylistApi, PlaylistApiClient } from '../services/playlistApi';

import { useProvider } from './useProvider';
import { usePlayer } from './PlayerProvider';

export interface ApisContextProps {
  svpApi: SvpApiClient;
  relatedApi: RelatedApiClient;
  vgtvApi: VGTVApiClient;
  spsApi: SpsApiClient;
  playlistApi: PlaylistApiClient;
}

const ApisContext = createContext<ApisContextProps>(undefined!);

const createSvpApiClient = (svpApiUrl: string, vendor: string) => {
  const client = createClient({ baseUrl: `${svpApiUrl}${vendor}` });

  return createSvpApi(client, 'vgp-player');
};
const createPlaylistApiClient = (playlistApiUrl: string, vendor: string) => {
  const client = createClient({ baseUrl: `${playlistApiUrl}${vendor}` });

  return createPlaylistApi(client);
};

const createRelatedApiClient = (relatedApiUrl: string, vendor: string) => {
  const client = createClient({ baseUrl: `${relatedApiUrl}${vendor}` });

  return createRelatedApi(client);
};

const createVGTVApiClient = (apiUrl: string, provider: StreamProvider) => {
  const client = createClient({ baseUrl: `${apiUrl}${provider}` });

  return createVGTVApi(client);
};

const SPS_CONFIG_BY_PROVIDER: Record<
  string,
  { publication: SpsPublication; domain: string }
> = {
  vgtv: { publication: SpsPublication.VG_TV, domain: 'vg.no' },
  ab: { publication: SpsPublication.AB_TV, domain: 'aftonbladet.se' },
  // `e24video` isn't added to sps-sdk types yet
  e24: { publication: 'e24video' as SpsPublication, domain: 'e24.no' },
};

export const getSpsConfig = (
  provider: StreamProvider | 'brandstudio'
): { publication: SpsPublication; domain: string } =>
  SPS_CONFIG_BY_PROVIDER[provider] ?? SPS_CONFIG_BY_PROVIDER.vgtv;

const createSpsApiClient = (
  provider: StreamProvider | 'brandstudio',
  isTpaasEnabled = false
) => {
  const { publication, domain } = getSpsConfig(provider);

  return createSpsApi(publication, domain, isTpaasEnabled);
};

const DEFAULT_SVP_API_URL = 'https://svp.vg.no/svp/api/v1/';
const DEFAULT_PLAYLIST_API_URL = 'https://playlist.stream.schibsted.media/';
const DEFAULT_RELATED_API_URL = 'https://related.stream.schibsted.media/';
const DEFAULT_VGTV_API_URL = 'https://api.vgtv.no/';

export const ApisProvider: FunctionComponent = ({ children }) => {
  const player = usePlayer();
  const { assetProvider, configProvider } = useProvider();

  const svpApi = useMemo(
    () =>
      createSvpApiClient(
        player.getRawConfig().api ?? DEFAULT_SVP_API_URL,
        assetProvider
      ),
    [player, assetProvider]
  );
  const playlistApi = useMemo(
    () => createPlaylistApiClient(DEFAULT_PLAYLIST_API_URL, assetProvider),
    [assetProvider]
  );
  const relatedApi = useMemo(
    () =>
      createRelatedApiClient(
        player.getRawConfig().relatedUrl ?? DEFAULT_RELATED_API_URL,
        assetProvider
      ),
    [player, assetProvider]
  );
  const vgtvApi = useMemo(
    () => createVGTVApiClient(DEFAULT_VGTV_API_URL, configProvider),
    [configProvider]
  );

  const spsApi = useMemo(
    () =>
      createSpsApiClient(
        configProvider,
        player.getRawConfig().pulse?.tpaas ?? false
      ),
    [configProvider]
  );

  const state = useMemo(() => {
    return {
      svpApi,
      relatedApi,
      vgtvApi,
      spsApi,
      playlistApi,
    };
  }, [svpApi, relatedApi, vgtvApi, spsApi, playlistApi]);

  return <ApisContext.Provider value={state}>{children}</ApisContext.Provider>;
};

export const useApis = () => {
  const apis = useContext(ApisContext);

  if (process.env.NODE_ENV !== 'production' && !apis) {
    console.warn(
      'APIs context is undefined, please verify you are calling useApis() as child of a <ApisProvider> component.'
    );
  }

  return apis;
};
